/**
 * Claude Code 利用量 dashboard panel.
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 全体 / チーム別 / 個人別の Claude Code 利用量 (token 系) を 1 画面に描画する presentation component。fetch は親が行い data を渡す。表示するトークン数は metric (cache 込み / Input+Output のみ) に応じて再集計する
 * @graph-connects none
 */

import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { formatCompact, formatNumber } from './formatters.js';
import { tokenAmount, tokenTypesForMetric } from './tokenMetric.js';
import type { CcUsageTokenMetric, CcUsageTokenType } from './tokenMetric.js';
import type {
  CcUsageStackedSeries,
  CcUsageStackedTimeseriesRow,
  CcUsageSummaryResponse,
  CcUsageTimeseriesByGroupResponse,
  CcUsageTimeseriesResponse,
} from '../../../types/api.js';

interface CcUsagePanelProps {
  summary: CcUsageSummaryResponse | undefined;
  isSummaryLoading: boolean;
  timeseries: CcUsageTimeseriesResponse | undefined;
  /** 積み上げ用 timeseries (team / nickname 別)。scope=member の時は undefined を渡す。 */
  timeseriesByGroup?: CcUsageTimeseriesByGroupResponse | undefined;
  /** timeseriesByGroup のフェッチ中フラグ */
  isTimeseriesByGroupLoading?: boolean;
  /** スコープが解決できない場合のメッセージ (例: "チームを選択してください") */
  emptyMessage?: string | null;
  /** 表示するトークン数の集計方法 (cache 込み合計 / Input+Output のみ) */
  metric: CcUsageTokenMetric;
}

/**
 * 積み上げチャート用のカラーパレット。
 * 凡例数が増えてもループして再利用する。同じ groupKey には毎回同じ色が当たるよう、
 * 累計 token desc の登場順で割り当てる。
 *
 * @graph-connects none
 */
const STACKED_CHART_PALETTE = [
  '#6366f1',
  '#10b981',
  '#f59e0b',
  '#3b82f6',
  '#ef4444',
  '#a855f7',
  '#14b8a6',
  '#f97316',
  '#ec4899',
  '#84cc16',
  '#0ea5e9',
  '#d946ef',
  '#22c55e',
  '#eab308',
  '#06b6d4',
  '#8b5cf6',
];

/** @graph-connects none */
const FALLBACK_COLOR = '#9ca3af';

/**
 * トークン種別ごとの表示メタ情報 (i18n キーと配色)。
 * 内訳バー・token 種別時系列チャートで共通利用する。
 * @graph-connects none
 */
const TOKEN_TYPE_META: Record<CcUsageTokenType, { labelKey: string; color: string }> = {
  inputTokens: { labelKey: 'ccUsage.metrics.inputTokens', color: '#6366f1' },
  outputTokens: { labelKey: 'ccUsage.metrics.outputTokens', color: '#10b981' },
  cacheCreationInputTokens: {
    labelKey: 'ccUsage.metrics.cacheCreationInputTokens',
    color: '#f59e0b',
  },
  cacheReadInputTokens: { labelKey: 'ccUsage.metrics.cacheReadInputTokens', color: '#3b82f6' },
};

/** @graph-connects none */
interface BreakdownMetric {
  label: string;
  value: number;
  color: string;
  share: number;
}

/**
 * 視認性を上げた共通の Recharts Tooltip コンテンツ。
 * @graph-connects none
 */
function chartTooltipStyle() {
  return {
    backgroundColor: 'var(--color-bg-elevated)',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    fontSize: '12px',
    boxShadow: '0 10px 25px -10px rgb(0 0 0 / 0.5)',
    zIndex: 50,
    position: 'relative',
  } as const;
}

/**
 * Recharts Tooltip wrapper の共通 style。
 * デフォルトの inset auto / position absolute だと Bar の数値ラベルや Legend に被って隠れることがあるので、
 * z-index を高く保ち pointerEvents を切って ホバー干渉を避ける。
 * @graph-connects none
 */
const CHART_TOOLTIP_WRAPPER_STYLE = { zIndex: 50, pointerEvents: 'none' } as const;

/** @graph-connects none */
const X_AXIS_PROPS = {
  stroke: 'var(--color-text-muted)',
  fontSize: 11,
  tickMargin: 8,
  axisLine: false,
  tickLine: false,
} as const;

/**
 * cc-usage 全チャート共通の Y 軸 props。
 * width を固定することで、上下に並んだチャート同士の plot 領域 (X 軸) の左端が揃う。
 *
 * @graph-connects none
 */
const Y_AXIS_PROPS = {
  stroke: 'var(--color-text-muted)',
  fontSize: 11,
  tickMargin: 4,
  axisLine: false,
  tickLine: false,
  width: 64,
} as const;

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-usage dashboard panel (presentation のみ)
 * @graph-connects none
 */
export function CcUsagePanel({
  summary,
  isSummaryLoading,
  timeseries,
  timeseriesByGroup,
  isTimeseriesByGroupLoading = false,
  emptyMessage,
  metric,
}: CcUsagePanelProps) {
  const { t } = useTranslation();

  if (emptyMessage) {
    return (
      <Card>
        <CardContent>
          <p className="text-center py-8 text-[var(--color-text-muted)]">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  if (isSummaryLoading) {
    // サマリーカード + チャートカードの形に沿ったスケルトンを表示する
    return (
      <div className="space-y-4">
        <Card padding="none">
          <CardContent className="!mt-0 flex flex-col gap-4 p-4 lg:flex-row lg:items-stretch">
            <div className="flex flex-col gap-2 lg:min-w-[260px] lg:border-r lg:border-[var(--color-border)] lg:pr-6">
              <Skeleton width={120} height={12} />
              <Skeleton width={180} height={36} />
              <Skeleton width={220} height={14} />
            </div>
            <div className="flex-1 space-y-3">
              <Skeleton variant="rectangular" height={8} />
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <Skeleton height={36} />
                <Skeleton height={36} />
                <Skeleton height={36} />
                <Skeleton height={36} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card padding="none">
          <CardContent className="h-[416px] !mt-3 px-3 pb-3">
            <Skeleton variant="rectangular" height="100%" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // 表示中の metric に含まれるトークン種別だけを集計・描画する。
  const tokenTypes = tokenTypesForMetric(metric);
  const grandTotal = summary ? tokenAmount(summary, metric) : 0;
  const headlineLabel =
    metric === 'inputOutput'
      ? t('ccUsage.metrics.inputOutputTokens')
      : t('ccUsage.metrics.totalTokens');

  // 主要数値 (集計方法に応じた合計トークン) と内訳 (種別ごとの値・占有率) を分離する。
  const breakdownMetrics: BreakdownMetric[] = tokenTypes.map((type) => {
    const value = summary?.[type] ?? 0;
    return {
      label: t(TOKEN_TYPE_META[type].labelKey),
      value,
      color: TOKEN_TYPE_META[type].color,
      share: grandTotal === 0 ? 0 : (value / grandTotal) * 100,
    };
  });

  const timeseriesData = (timeseries?.data ?? []).map((point) => ({
    bucket: point.bucket,
    inputTokens: point.inputTokens,
    outputTokens: point.outputTokens,
    cacheCreationInputTokens: point.cacheCreationInputTokens,
    cacheReadInputTokens: point.cacheReadInputTokens,
  }));

  // 積み上げ series は metric に応じた合計の降順で並べ直す (色はこの順位に従って割り当てる)。
  const stackedSeries: CcUsageStackedSeries[] = timeseriesByGroup
    ? [...timeseriesByGroup.series].sort(
        (a, b) => tokenAmount(b.tokens, metric) - tokenAmount(a.tokens, metric),
      )
    : [];

  const stackedTitle = t('ccUsage.stackedByGroup.title');

  return (
    <div className="space-y-4">
      {/* ── サマリ ── */}
      <Card padding="none">
        <CardContent className="!mt-0 flex flex-col gap-4 p-4 lg:flex-row lg:items-stretch">
          {/* 主要数値 */}
          <div className="flex flex-col gap-1 lg:min-w-[260px] lg:border-r lg:border-[var(--color-border)] lg:pr-6">
            <span className="text-[10px] uppercase tracking-[0.16em] text-[var(--color-text-muted)]">
              {headlineLabel}
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold tabular-nums text-[var(--color-text-primary)] lg:text-4xl">
                {formatCompact(grandTotal)}
              </span>
              <span className="text-xs text-[var(--color-text-muted)]">tokens</span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--color-text-muted)]">
              <span>
                {t('ccUsage.metrics.messageCount')}{' '}
                <span className="font-semibold tabular-nums text-[var(--color-text-secondary)]">
                  {formatNumber(summary?.messageCount ?? 0)}
                </span>
              </span>
              <span aria-hidden="true">·</span>
              <span>
                {t('ccUsage.breakdown.uniqueUsers')}{' '}
                <span className="font-semibold tabular-nums text-[var(--color-text-secondary)]">
                  {formatNumber(summary?.uniqueUsers ?? 0)}
                </span>
              </span>
              <span aria-hidden="true">·</span>
              <span>
                {t('ccUsage.breakdown.repository')}{' '}
                <span className="font-semibold tabular-nums text-[var(--color-text-secondary)]">
                  {formatNumber(summary?.uniqueRepositories ?? 0)}
                </span>
              </span>
            </div>
          </div>

          {/* 内訳バー */}
          <div className="flex-1">
            <div className="flex h-2 w-full overflow-hidden rounded-full bg-[var(--color-bg-base)]">
              {breakdownMetrics.map((m) =>
                m.share > 0 ? (
                  <div
                    key={m.label}
                    style={{ width: `${m.share.toString()}%`, backgroundColor: m.color }}
                    aria-label={`${m.label} ${m.share.toFixed(1)}%`}
                  />
                ) : null,
              )}
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
              {breakdownMetrics.map((m) => (
                <div key={m.label} className="flex flex-col gap-0.5">
                  <dt className="flex items-center gap-1.5 text-[11px] text-[var(--color-text-muted)]">
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-full"
                      style={{ backgroundColor: m.color }}
                      aria-hidden="true"
                    />
                    <span className="truncate">{m.label}</span>
                  </dt>
                  <dd className="flex items-baseline gap-1.5">
                    <span className="text-sm font-semibold tabular-nums text-[var(--color-text-primary)]">
                      {formatCompact(m.value)}
                    </span>
                    <span className="text-[10px] tabular-nums text-[var(--color-text-muted)]">
                      {m.share.toFixed(0)}%
                    </span>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </CardContent>
      </Card>

      {/* ── 時系列チャート ──
        timeseriesByGroup がある場合はグループ別の積み上げ、
        ない場合は token 種別の時系列 (timeseries) にフォールバックする。
        いずれも metric に含まれるトークン種別だけを積み上げる。
      */}
      {timeseriesByGroup ? (
        <ChartCard title={stackedTitle} loading={isTimeseriesByGroupLoading}>
          {timeseriesByGroup.rows.length === 0 ? (
            <p className="flex h-full items-center justify-center text-sm text-[var(--color-text-muted)]">
              {t('ccUsage.empty')}
            </p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={timeseriesByGroup.rows}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="var(--color-border)"
                  vertical={false}
                />
                <XAxis dataKey="bucket" {...X_AXIS_PROPS} />
                <YAxis tickFormatter={formatCompact} {...Y_AXIS_PROPS} />
                <Tooltip
                  formatter={formatBarValue}
                  contentStyle={chartTooltipStyle()}
                  wrapperStyle={CHART_TOOLTIP_WRAPPER_STYLE}
                  cursor={{ fill: 'var(--color-bg-hover)', opacity: 0.4 }}
                />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" />
                {stackedSeries.map((series, idx) => (
                  <Bar
                    key={series.key}
                    dataKey={(row: CcUsageStackedTimeseriesRow) => {
                      const tokens = row.values[series.key];
                      return tokens ? tokenAmount(tokens, metric) : 0;
                    }}
                    name={series.label}
                    stackId="by-group"
                    fill={
                      STACKED_CHART_PALETTE[idx % STACKED_CHART_PALETTE.length] ?? FALLBACK_COLOR
                    }
                    radius={idx === stackedSeries.length - 1 ? [3, 3, 0, 0] : 0}
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      ) : (
        <ChartCard title={t('ccUsage.timeseries.title')}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={timeseriesData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
              <XAxis dataKey="bucket" {...X_AXIS_PROPS} />
              <YAxis tickFormatter={formatCompact} {...Y_AXIS_PROPS} />
              <Tooltip
                formatter={formatBarValue}
                contentStyle={chartTooltipStyle()}
                wrapperStyle={CHART_TOOLTIP_WRAPPER_STYLE}
                cursor={{ fill: 'var(--color-bg-hover)', opacity: 0.4 }}
              />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" />
              {tokenTypes.map((type, idx) => (
                <Bar
                  key={type}
                  dataKey={type}
                  name={t(TOKEN_TYPE_META[type].labelKey)}
                  stackId="tokens"
                  fill={TOKEN_TYPE_META[type].color}
                  radius={idx === tokenTypes.length - 1 ? [3, 3, 0, 0] : 0}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
    </div>
  );
}

/**
 * Recharts Tooltip formatter: Bar の token 値を数値表示する。
 * @graph-connects none
 */
function formatBarValue(value: unknown): string {
  if (typeof value !== 'number') return '';
  return formatNumber(value);
}

/**
 * チャートカードの共通枠。タイトル + 固定高さ (h-[416px]) を提供する。
 * @graph-connects none
 */
function ChartCard({
  title,
  loading = false,
  children,
}: {
  title: string;
  loading?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Card padding="none">
      <CardHeader className="!mt-0 px-4 pt-4">
        <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-text-muted)]">
          {title}
        </h2>
      </CardHeader>
      <CardContent className="h-[416px] !mt-3 px-3 pb-3">
        {loading ? <Skeleton variant="rectangular" height="100%" /> : children}
      </CardContent>
    </Card>
  );
}
